import { useEffect, useRef } from "react";
import { ResidentReport } from "../components/ResidentReport";
import { useResidentReportReader } from "./useResidentReportReader";
import type { ResidentReportBatchLoader } from "../types/resident-report.types";

interface ResidentReportReaderProps {
  loadBatch: ResidentReportBatchLoader;
  resetKey: string;
  pageSize?: number;
}

export function ResidentReportReader({
  loadBatch,
  resetKey,
  pageSize = 25,
}: ResidentReportReaderProps) {
  const rootRef = useRef<HTMLDivElement>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);
  const { reports, hasMore, error, loading, loadMore, reset } = useResidentReportReader(
    loadBatch,
    resetKey,
    pageSize,
  );

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || !hasMore || typeof IntersectionObserver === "undefined") return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) loadMore();
      },
      { root: rootRef.current, rootMargin: "900px 0px" },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, loadMore]);

  return (
    <section
      className="resident-report-reader"
      ref={rootRef}
      aria-label="Resident report reader"
    >
      <div className="resident-report-reader__pages">
        {reports.map((report) => (
          <div
            className="resident-report-reader__page"
            key={report.resident.id}
          >
            <ResidentReport data={report} />
          </div>
        ))}
      </div>
      {error ? (
        <div className="resident-report-reader__status">
          {error}{" "}
          <button
            type="button"
            onClick={reset}
          >
            Retry
          </button>
        </div>
      ) : null}
      {loading ? (
        <div className="resident-report-reader__status">Loading resident reports…</div>
      ) : null}
      {!loading && reports.length === 0 && !error ? (
        <div className="resident-report-reader__status">
          No resident reports match these filters.
        </div>
      ) : null}
      <div
        className="resident-report-reader__sentinel"
        ref={sentinelRef}
      />
    </section>
  );
}
